import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { styled } from "@mui/system";
import {
  getISODate,
  useAuth,
  customTooltipProps,
  convertToTimezone,
  MonthFilter,
} from "../../sdk";
import {
  Box as BoxPR,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Nodata } from "../../sdk/components/Nodata";
import { Box, Typography, Paper } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Schedule } from "@mui/icons-material";
import usePickUp from "../hooks/usePickUp";
import { CombinedPickupTable } from "./PickupTable";
import { ClassWrapper } from "../../Pace/components/Styles";

export const PickUpPage = styled(Box)`
  padding: 60px 30px 40px 100px;
  width: 100%;
  .dropdownStyle {
    background: #160e39;
    border-radius: 4px;
    color: white;
  }
  .icon {
    fill: white;
    display: none;
  }
`;

const StyledCalenderWrapper = styled(BoxPR)({
  borderRadius: "8px",
  boxShadow: "0px 4px 4px rgba(48, 111, 188, 0.2)",
  "&:hover>*": {
    color: "#ffffff",
    ">input": {
      backgroundColor: "#306fbc",
      color: "#ffffff !important",
    },
  },
});

const inputStyle = {
  textAlign: "left",
  marginBottom: "2px",
  fontSize: "14px",
  borderRadius: "10px",
  color: "#000000",
  outline: "none",
  height: "30px",
  border: "none",
  font: "bold 16px / 20px Roboto",
  cursor: "pointer",
  width: "140px",
  paddingLeft: "14px",
};

const StyledDatePicker = ({ value, onChange, maxDate }) => (
  <StyledCalenderWrapper>
    <DatePicker
      inputFormat={"dd/MM/yyyy"}
      style={{ zIndex: 98 }}
      value={value}
      onChange={onChange}
      renderInput={({ inputRef, inputProps, InputProps }) => (
        <BoxPR
          className="my-custom-input-class"
          sx={{
            display: "flex",
            position: "relative",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <input ref={inputRef} {...inputProps} readOnly style={inputStyle} />
          <BoxPR
            className="date-button-container"
            sx={{ position: "absolute", right: 0, width: "100%" }}
          >
            {InputProps?.endAdornment}
          </BoxPR>
        </BoxPR>
      )}
      maxDate={maxDate}
    />
  </StyledCalenderWrapper>
);

export const PickerPaper = styled(Paper)`
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LabelNew = styled(Typography)`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 0px;
  color: #163a90;
  padding-right: 0px;
  text-transform: uppercase;
`;

const ScheduleIconDisabled = styled(Schedule)`
  color: #6f6f6f;
`;

export const Header = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 52px;
  background: #ffffff 0% 0% no-repeat padding-box;
  font: normal normal bold 16px/20px Roboto;
  letter-spacing: 0px;
  color: #281e53;
  margin: 30px 4px;
`;

export const DATE = styled(Box)`
  box-shadow: 0px 4px 4px rgba(48, 111, 188, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 4px 12px 4px 16px;
`;
export const SubHeaderNew = styled(Box)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 24px;
`;

const TimeSelect = ({
  timeOptions,
  selectedFileId,
  onFileIdChange,
  hotelTimeZone,
}) => (
  <PickerPaper elevation={2} style={{ height: "30px" }}>
    <Select
      disableUnderline
      variant="standard"
      IconComponent="none"
      disabled={!timeOptions || timeOptions.length === 0}
      endAdornment={
        timeOptions && timeOptions.length > 0 ? (
          <Schedule
            style={{
              width: "20px",
              height: "20px",
              marginLeft: "-18px",
              color: "#306fbc",
            }}
          />
        ) : (
          <ScheduleIconDisabled
            style={{
              color: "#6f6f6f",
              width: "20px",
              height: "20px",
            }}
          />
        )
      }
      inputProps={{
        classes: {
          icon: "icon",
        },
      }}
      classes={{
        paper: "dropdownStyle",
      }}
      value={selectedFileId}
      onChange={(e) => onFileIdChange(e.target.value)}
      sx={{
        minWidth: 120,
        height: 30,
        fontSize: 14,
        fontFamily: "Roboto",
        fontWeight: 500,
        textTransform: "uppercase",
        "&.Mui-focused": {
          backgroundColor: "transparent",
        },
        "& .MuiSelect-select:focus": {
          backgroundColor: "transparent",
        },
        "& .MuiSelect-select": {
          backgroundColor: "transparent",
          fontWeight: 500,
          textTransform: "uppercase",
        },
      }}
    >
      {timeOptions && timeOptions.length > 0 ? (
        timeOptions.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            <Tooltip
              title={`${hotelTimeZone}: ${convertToTimezone(
                option?.uploadedOn,
                hotelTimeZone,
                "DD/MM/YYYY hh:mm a"
              )}`}
              slotProps={customTooltipProps}
            >
              <Typography sx={{ textTransform: "uppercase", fontWeight: 500 }}>
                {convertToTimezone(option.uploadedOn, hotelTimeZone, "hh:mm a")}
              </Typography>
            </Tooltip>
          </MenuItem>
        ))
      ) : (
        <MenuItem value="" disabled>
          -
        </MenuItem>
      )}
    </Select>
  </PickerPaper>
);

export const PickUp = ({ setPageHeader }) => {
  useEffect(() => {
    setPageHeader("PickUp");
  }, [setPageHeader]);

  const { hotelId } = useParams();
  const {
    getFileByFromDate,
    fromTimeOption,
    getFileByToDate,
    toTimeOption,
    marketSegements,
    marketSegmentsOptions,
    getPickUpData,
  } = usePickUp();
  const { currentHotel } = useAuth();
  const hotelTimeZone = currentHotel?.timezone;

  const [selectedFromFileId, setSelectedFromFileId] = useState("");
  const [selectedToFileId, setSelectedToFileId] = useState("");
  const [selectedSegmentId, setSelectedSegmentId] = useState("");
  const [pickupData, setPickupData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [fromDate, setFromDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 1))
  );

  const [toDate, setToDate] = useState(new Date());

  const { token } = useAuth();
  const maximumDate = new Date();

  // Get totals from API
  const totals = useMemo(() => {
    if (!pickupData) return null;

    return {
      rooms: {
        from: pickupData.totalFromRoomBooked,
        to: pickupData.totalToRoomBooked,
        pickup: pickupData.totalToRoomBooked - pickupData.totalFromRoomBooked,
      },
      adr: {
        from: pickupData.totalFromAdr,
        to: pickupData.totalToAdr,
        pickup: pickupData.totalToAdr - pickupData.totalFromAdr,
      },
      revenue: {
        from: pickupData.totalFromRevenue,
        to: pickupData.totalToRevenue,
        pickup: pickupData.totalToRevenue - pickupData.totalFromRevenue,
      },
    };
  }, [pickupData]);

  useEffect(() => {
    if (fromDate) {
      const formattedDate = getISODate(fromDate).split("T")[0];
      getFileByFromDate(formattedDate);
    }
  }, [fromDate, getFileByFromDate]);

  useEffect(() => {
    if (toDate) {
      const formattedDate = getISODate(toDate).split("T")[0];
      getFileByToDate(formattedDate);
    }
  }, [toDate, getFileByToDate]);

  useEffect(() => {
    if (fromTimeOption && fromTimeOption.length > 0) {
      setSelectedFromFileId(fromTimeOption[fromTimeOption.length - 1].id);
    }
  }, [fromTimeOption]);

  useEffect(() => {
    if (toTimeOption && toTimeOption.length > 0) {
      setSelectedToFileId(toTimeOption[toTimeOption.length - 1].id);
    }
  }, [toTimeOption]);

  useEffect(() => {
    marketSegements();
  }, [marketSegements]);

  useEffect(() => {
    if (marketSegmentsOptions && marketSegmentsOptions.length > 0) {
      setSelectedSegmentId(marketSegmentsOptions[0]?.segmentId || "");
    }
  }, [marketSegmentsOptions]);

  // Fetch pickup data when parameters change
  useEffect(() => {
    const fetchData = async () => {
      if (
        selectedFromFileId &&
        selectedToFileId &&
        selectedSegmentId &&
        selectedMonth
      ) {
        setIsLoading(true);
        try {
          const firstDay = new Date(
            selectedMonth.getFullYear(),
            selectedMonth.getMonth(),
            1
          );
          const formattedDate = getISODate(firstDay).split("T")[0];
          const data = await getPickUpData(
            selectedFromFileId,
            selectedToFileId,
            selectedSegmentId,
            formattedDate
          );
          setPickupData(data);
        } catch (error) {
          console.error("Failed to fetch pickup data:", error);
          setPickupData(null);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchData();
  }, [
    selectedFromFileId,
    selectedToFileId,
    selectedSegmentId,
    selectedMonth,
    getPickUpData,
  ]);

  useEffect(() => {
    if (token && hotelId) {
      window.scrollTo(0, 0);
    } else {
      return;
    }
  }, [token, hotelId]);

  return (
    <PickUpPage style={{ background: "#fff", minHeight: "100vh" }}>
      <ClassWrapper>
        <Header>
          <SubHeaderNew>
            <DATE>
              <MonthFilter value={selectedMonth} onChange={setSelectedMonth} />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledDatePicker
                  value={fromDate}
                  onChange={setFromDate}
                  maxDate={maximumDate}
                />

                <TimeSelect
                  timeOptions={fromTimeOption}
                  selectedFileId={selectedFromFileId}
                  onFileIdChange={setSelectedFromFileId}
                  hotelTimeZone={hotelTimeZone}
                />

                <LabelNew>To</LabelNew>
                <StyledDatePicker
                  value={toDate}
                  onChange={setToDate}
                  maxDate={maximumDate}
                />

                <TimeSelect
                  timeOptions={toTimeOption}
                  selectedFileId={selectedToFileId}
                  onFileIdChange={setSelectedToFileId}
                  hotelTimeZone={hotelTimeZone}
                />

                <PickerPaper elevation={2} style={{ height: "30px" }}>
                  <Select
                    disableUnderline
                    variant="standard"
                    value={selectedSegmentId}
                    onChange={(e) => setSelectedSegmentId(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    sx={{
                      minWidth: 150,
                      height: 30,
                      fontSize: 14,
                      fontFamily: "Roboto",
                      "&.Mui-focused": {
                        backgroundColor: "transparent",
                      },
                      "& .MuiSelect-select:focus": {
                        backgroundColor: "transparent",
                      },
                      "& .MuiSelect-select": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {marketSegmentsOptions &&
                    marketSegmentsOptions.length > 0 ? (
                      marketSegmentsOptions.map((segment) => (
                        <MenuItem
                          key={segment.segmentId}
                          value={segment.segmentId}
                        >
                          {segment.systemName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        No segments available
                      </MenuItem>
                    )}
                  </Select>
                </PickerPaper>
              </LocalizationProvider>
            </DATE>
          </SubHeaderNew>
        </Header>

        {isLoading ? (
          <BoxPR
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress style={{ color: "#306fbc" }} />
          </BoxPR>
        ) : !pickupData ||
          !pickupData.roomNights ||
          pickupData.roomNights.length === 0 ? (
          <Nodata />
        ) : (
         <BoxPR
            sx={{
              marginBottom: "40px",
            }}
          >
            <CombinedPickupTable
              data={pickupData}
              fromDate={fromDate}
              toDate={toDate}
              totals={totals}
            />
          </BoxPR>
        )}
      </ClassWrapper>
    </PickUpPage>
  );
};
